import {
  Box,
  Button,
  Drawer,
  DrawerProps,
  IconButton,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import { HeaderMobile } from '@/components/Header/HeaderMobile';
import { Icon } from '@/components/Icon/Icon';
import { DrawerHeader } from '@/components/Layout';
import { DesktopOnly, MobileOnly } from '@/components/Layout/Responsive';

import { ACCOUNT_TYPE } from '@/constants/account';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs';
import { selectIsMinorAccount } from '@/redux/user-data/selectors';

import { AccountTypeDetails, AccountTypeStatus } from '@/types/api/account';
import { Tenant } from '@/types/common';
import { Language } from '@/types/i18n';

import { getDataTestId } from '@/utils/browser';
import { isCaymanDomain } from '@/utils/domain';
import { renderHTMLText } from '@/utils/string';
import { isCaymanUser } from '@/utils/user';

import { getAccountHighlightFigure } from '../utils/account';
import { JointProceedButtons } from './JointProcessButtons';

import { AccountTypeProceedButton } from '../components/AccountTypeCard.styled';
import {
  AccountTypeTitle,
  DetailedPrice,
  DetailsContent,
  DrawerContainer,
  DrawerLogo,
} from '../components/AccountTypeForm.styled';

type AccountTypeDetailsDrawerProps = {
  data: AccountTypeDetails;
  isSelected?: boolean;
  isOnlyAccountType?: boolean;
  isDisabledSelect?: boolean;
  onProceed: () => void;
  onSelect?: () => void;
  onClose: () => void;
  onNotNow: () => void;
  onContinue: () => void;
} & Omit<DrawerProps, 'onClose'>;

export function AccountTypeDetailsDrawer({
  data,
  isOnlyAccountType,
  isSelected,
  isDisabledSelect = false,
  onSelect,
  onProceed,
  onNotNow,
  onContinue,
  ...props
}: AccountTypeDetailsDrawerProps) {
  const { t, translateIfExists, language } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);
  const { logo, currencyPlacement } = useAppSelector(selectConfigsState);
  const isMinorAccount = useAppSelector(selectIsMinorAccount);
  const { figure, figureSubText, figureDescription } = useMemo(
    () => getAccountHighlightFigure(data, language),
    [data, language],
  );

  const handleSelect = () => {
    onSelect?.();
    props.onClose();
  };

  const renderActionButton = () => {
    if (!data.isEligible) {
      return (
        <Button
          {...getDataTestId(`account-type-details-cancel-button`)}
          variant="outlined"
          fullWidth
          onClick={props.onClose}
        >
          {t('common.cancel')}
        </Button>
      );
    }

    if (isOnlyAccountType) {
      return (
        <Button
          {...getDataTestId(`account-type-details-proceed-button`)}
          variant="contained"
          fullWidth
          onClick={onProceed}
          disabled={isDisabledSelect}
        >
          {t('accountTypeForm.detailsDrawer.button.proceedWith', {
            name: data.description,
          })}
        </Button>
      );
    }

    return (
      <AccountTypeProceedButton
        {...getDataTestId(`account-type-details-select-button`)}
        variant="outlined"
        fullWidth
        isCaymanTenant={isCaymanUser(user)}
        onClick={isDisabledSelect ? undefined : handleSelect}
        disabled={isDisabledSelect}
      >
        {!isSelected
          ? t('accountTypeForm.detailsDrawer.button.select', {
              name: data.description,
            })
          : t('accountTypeForm.detailsDrawer.button.unSelect', {
              name: data.description,
            })}
      </AccountTypeProceedButton>
    );
  };

  const renderAdditionalInfo = () => {
    if (!data.additionalInfo) return null;

    const isExcludedTenants = [
      Tenant.SaintLucia,
      Tenant.Grenada,
      Tenant.Barbados,
    ].includes(user?.tenant as Tenant);

    // Hide additional info for CHQ type (e-free account) in excluded tenants
    if (isExcludedTenants && data.type === ACCOUNT_TYPE.CHQ) {
      return null;
    }

    return (
      <Typography variant="caption" mb={1.5}>
        {renderHTMLText(data.additionalInfo)}
      </Typography>
    );
  };

  // Remove <b> tag because we are using noteDescription for both card and detail but only card is using b tag
  const figureDescriptionText = useMemo(() => {
    return figureDescription?.replace(/(<\/?b\b[^>]*>)/g, '');
  }, [figureDescription]);

  return (
    <Drawer {...props}>
      <HeaderMobile
        bgColor={theme => theme.palette.background.paper}
        leftSlot={null}
        centerSlot={null}
        rightSlot={
          <IconButton onClick={props.onClose}>
            <Icon name="close" color={theme => theme.palette.neutral[700]} />
          </IconButton>
        }
      />
      <DrawerHeader>
        <Box />
        <IconButton onClick={props.onClose}>
          <Icon name="close" color={theme => theme.palette.neutral[700]} />
        </IconButton>
      </DrawerHeader>
      <DrawerContainer>
        <MobileOnly>
          <Box
            sx={
              isCaymanDomain
                ? { marginTop: theme => theme.spacing(12) }
                : { marginTop: 0 }
            }
          >
            <DrawerLogo src={logo} />
          </Box>
        </MobileOnly>
        {isCaymanUser(user) && (
          <DesktopOnly>
            <DrawerLogo src={logo} />
          </DesktopOnly>
        )}
        <AccountTypeTitle gap={1}>
          <Box pl={2}>
            <Typography variant="headlineH2">{data.name}</Typography>
          </Box>
          {figure !== undefined && (
            <DetailedPrice>
              <Typography variant="headlineH3">
                <Box component={'span'} sx={{ whiteSpace: 'nowrap' }}>
                  {currencyPlacement[language as Language] === 'after' ? (
                    <>
                      {figure}
                      {figureSubText && ` ${figureSubText}`}
                    </>
                  ) : (
                    <>
                      {figureSubText && ` ${figureSubText}`}
                      {figure}
                    </>
                  )}
                </Box>
                {figureDescription && (
                  <>
                    <br />
                    <Typography variant="paragraphP2">
                      {renderHTMLText(
                        translateIfExists(figureDescriptionText || '') ||
                          figureDescriptionText ||
                          '',
                      )}
                    </Typography>
                  </>
                )}
              </Typography>
            </DetailedPrice>
          )}
        </AccountTypeTitle>
        <DetailsContent>
          {renderHTMLText(data.detail)}
          {renderAdditionalInfo()}
          {data.status === AccountTypeStatus.Active && (
            <>
              {isMinorAccount ? (
                <JointProceedButtons
                  onContinue={onContinue}
                  onNotNow={onNotNow}
                />
              ) : (
                <Box mt={4}>{renderActionButton()}</Box>
              )}
            </>
          )}
        </DetailsContent>
      </DrawerContainer>
    </Drawer>
  );
}
